import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Check, X, UserPlus } from 'lucide-react';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { toast } from 'react-toastify';
import { useMessageHubStore } from '../../store/messageHubStore';
import { MessageHubShell } from './MessageHubShell';
import { MESSAGE_HUB_PAGE_SIZE } from './MessageHubListPagination';
import {
  MessageHubListColumn,
  MessageHubDetailPanel,
  MessageHubEmptyDetail,
  MessageHubListItem,
  HubAvatar,
  HubStatusBadge,
  formatHubTime,
} from './MessageHubShared';

interface Recruiter {
  id: number;
  fullName: string;
  company: string;
  profileImg?: string | null;
}

interface MessageRequest {
  id: string;
  recruiter: Recruiter;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: string;
}

function requestStatusVariant(status: MessageRequest['status']): 'warning' | 'success' | 'neutral' {
  if (status === 'pending') return 'warning';
  if (status === 'accepted') return 'success';
  return 'neutral';
}

export default function MessageRequestList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<MessageRequest | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const [search, setSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const requests = useMessageHubStore((state) => state.requests) as MessageRequest[];
  const loading = useMessageHubStore((state) => state.requestsLoading);
  const fetchRequests = useMessageHubStore((state) => state.fetchRequests);
  const setRequests = useMessageHubStore((state) => state.setRequests);

  const handleAction = async (id: string, action: 'accept' | 'decline') => {
    setProcessingId(id);

    try {
      const url =
        action === 'accept'
          ? API_ROUTES.MESSAGESHUB.ACCEPT_REQUEST(id)
          : API_ROUTES.MESSAGESHUB.DECLINE_REQUEST(id);

      const res = await axiosInstance.post(url);

      if (res.data?.success) {
        setRequests(
          requests.map((r) =>
            r.id === id ? { ...r, status: action === 'accept' ? 'accepted' : 'declined' } : r
          )
        );

        if (selectedRequest?.id === id) {
          setSelectedRequest((prev) =>
            prev
              ? {
                  ...prev,
                  status: action === 'accept' ? 'accepted' : 'declined',
                }
              : prev
          );
        }

        toast.success(`Request ${action}ed`);
      }
    } catch {
      toast.error(`Failed to ${action}`);
    } finally {
      setProcessingId(null);
    }
  };

  useEffect(() => {
    void fetchRequests().catch(() => toast.error('Failed to load message requests'));
  }, [fetchRequests]);

  useEffect(() => {
    const targetId = searchParams.get('request');
    if (!targetId || requests.length === 0) return;
    const match = requests.find((r) => String(r.id) === String(targetId));
    if (!match) return;
    setSelectedRequest(match);
    setMobileView('detail');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('request');
        return next;
      },
      { replace: true }
    );
  }, [requests, searchParams, setSearchParams]);

  useEffect(() => {
    setListPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter(
      (r) =>
        r.recruiter.fullName.toLowerCase().includes(q) ||
        r.recruiter.company.toLowerCase().includes(q)
    );
  }, [requests, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / MESSAGE_HUB_PAGE_SIZE));
  const safeListPage = Math.min(listPage, totalPages);
  const pagedFiltered = useMemo(
    () =>
      filtered.slice(
        (safeListPage - 1) * MESSAGE_HUB_PAGE_SIZE,
        safeListPage * MESSAGE_HUB_PAGE_SIZE
      ),
    [filtered, safeListPage]
  );

  const listColumn = (
    <MessageHubListColumn
      title="Requests"
      searchPlaceholder="Search requests…"
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      loadingLabel="Message Requests"
      emptyMessage="No requests found."
      listPage={safeListPage}
      totalItems={filtered.length}
      onPageChange={setListPage}
    >
      {pagedFiltered.map((req) => (
        <MessageHubListItem
          key={req.id}
          active={selectedRequest?.id === req.id}
          onClick={() => {
            setSelectedRequest(req);
            setMobileView('detail');
          }}
          avatar={<HubAvatar name={req.recruiter.fullName} />}
          name={req.recruiter.fullName}
          meta={req.recruiter.company}
          preview="Wants to connect with you"
          time={formatHubTime(req.createdAt)}
          badges={<HubStatusBadge status={req.status} variant={requestStatusVariant(req.status)} />}
        />
      ))}
    </MessageHubListColumn>
  );

  const detailColumn = selectedRequest ? (
    <MessageHubDetailPanel
      onBack={() => setMobileView('list')}
      title={selectedRequest.recruiter.fullName}
      subtitle={selectedRequest.recruiter.company}
      avatar={<HubAvatar name={selectedRequest.recruiter.fullName} large />}
      footer={
        selectedRequest.status === 'pending' ? (
          <div className="mh-detail-actions">
            <button
              type="button"
              className="mh-btn mh-btn--primary"
              onClick={() => void handleAction(selectedRequest.id, 'accept')}
              disabled={processingId === selectedRequest.id}
            >
              {processingId === selectedRequest.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Accept
            </button>
            <button
              type="button"
              className="mh-btn mh-btn--danger"
              onClick={() => void handleAction(selectedRequest.id, 'decline')}
              disabled={processingId === selectedRequest.id}
            >
              {processingId === selectedRequest.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Decline
            </button>
          </div>
        ) : undefined
      }
    >
      <div className="mh-detail-card">
        <p className="mh-detail-card-label">Request status</p>
        <HubStatusBadge
          status={selectedRequest.status}
          variant={requestStatusVariant(selectedRequest.status)}
        />
        <p className="mh-detail-card-body">Requested {formatHubTime(selectedRequest.createdAt)}</p>
        {selectedRequest.status !== 'pending' ? (
          <p className="mh-detail-card-muted">
            This request has been {selectedRequest.status}. No further action is needed.
          </p>
        ) : (
          <p className="mh-detail-card-muted">
            Accept to start messaging, or decline to dismiss this request.
          </p>
        )}
      </div>
    </MessageHubDetailPanel>
  ) : (
    <MessageHubEmptyDetail
      icon={<UserPlus className="h-7 w-7" />}
      title="Select a request"
      description="Tap a request to accept or decline, or review its details."
    />
  );

  return (
    <MessageHubShell
      active="requests"
      mobileView={mobileView}
      layoutRevision={selectedRequest?.id ?? 'empty'}
      listColumn={listColumn}
      detailColumn={detailColumn}
    />
  );
}
